import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  TrendingUp, Users, Briefcase, GraduationCap, MonitorPlay,
  Newspaper, Headset, ArrowRight,
  Sparkles, BarChart3,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

/* ────────────────── tiny inline visual widgets ────────────────── */

const SignalPreview = ({ symbol = 'XAU/USD', side = 'BUY', isRTL = false }) => (
  <div className="relative bg-white rounded-2xl border border-flame-100/80 p-4 shadow-[0_8px_30px_rgba(20,15,10,0.06)] overflow-hidden">
    <div className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} px-2.5 py-1 text-[10px] font-black tracking-widest rounded-bl-xl ${side === 'BUY' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>
      {side}
    </div>
    <div className="flex items-center justify-between mb-3">
      <span className="text-[11px] text-ink-500 font-bold uppercase tracking-wider">{isRTL ? 'إشارة اليوم' : 'Today\'s signal'}</span>
      <span className="text-sm font-black text-ink-900">{symbol}</span>
    </div>
    <svg viewBox="0 0 200 50" className="w-full h-12 mb-3" preserveAspectRatio="none">
      <defs>
        <linearGradient id="sigGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#D85308" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#D85308" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points="0,40 20,32 40,36 60,28 80,30 100,22 120,20 140,16 160,10 180,12 200,6"
        fill="none" stroke="#D85308" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="0,40 20,32 40,36 60,28 80,30 100,22 120,20 140,16 160,10 180,12 200,6 200,50 0,50"
        fill="url(#sigGrad)" />
    </svg>
    <div className="grid grid-cols-3 gap-2 text-center">
      {[
        { l: isRTL ? 'دخول' : 'Entry', v: '2,028.40', c: 'text-ink-900' },
        { l: isRTL ? 'الهدف' : 'TP',    v: '2,041.20', c: 'text-emerald-600' },
        { l: isRTL ? 'الوقف' : 'SL',    v: '2,021.10', c: 'text-rose-600' },
      ].map((x) => (
        <div key={x.l} className="bg-flame-50/60 rounded-lg py-1.5">
          <div className="text-[9px] font-bold text-ink-500 uppercase tracking-wider">{x.l}</div>
          <div className={`text-[11px] font-mono font-black ${x.c}`}>{x.v}</div>
        </div>
      ))}
    </div>
  </div>
);

const BeginnerCommunityPreview = ({ isRTL = false }) => {
  const avatars = ['#D85308', '#FFB763', '#5BB6F0', '#A99BFF'];
  const msgs = isRTL
    ? ['ما الفرق بين السبريد والعمولة؟', 'كيف أحسب حجم اللوت المناسب؟', '👍 شكراً، فهمت الآن']
    : ['What\'s spread vs. commission?', 'How do I calculate lot size?', '👍 Got it, thanks!'];
  return (
    <div className="bg-white rounded-2xl border border-flame-100/80 p-4 shadow-[0_8px_30px_rgba(20,15,10,0.06)]">
      <div className="flex items-center justify-between mb-3">
        <div className={`flex items-center ${isRTL ? '-space-x-reverse -space-x-2' : '-space-x-2'}`}>
          {avatars.map((c, i) => (
            <div key={i} className="w-7 h-7 rounded-full ring-2 ring-white flex items-center justify-center text-[10px] font-black text-white" style={{ backgroundColor: c }}>
              {String.fromCharCode(65 + i)}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          {isRTL ? '1,240 متّصل' : '1,240 online'}
        </div>
      </div>
      <div className="space-y-2">
        {msgs.map((m, i) => (
          <div key={i} className={`text-[11px] px-3 py-1.5 rounded-2xl max-w-[85%] ${
            i % 2 === 0
              ? 'bg-flame-50 text-ink-700 ms-auto'
              : 'bg-ink-900 text-white'
          } ${isRTL && i % 2 === 0 ? 'me-auto ms-0' : ''}`}>
            {m}
          </div>
        ))}
      </div>
    </div>
  );
};

const ProCommunityPreview = ({ isRTL = false }) => (
  <div className="bg-white rounded-2xl border border-flame-100/80 p-4 shadow-[0_8px_30px_rgba(20,15,10,0.06)]">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] font-black uppercase tracking-widest text-flame-700">{isRTL ? 'تحليل احترافي' : 'Pro analysis'}</span>
      <span className="text-[10px] text-ink-500 font-bold">EUR/USD · 4H</span>
    </div>
    <div className="relative h-24 bg-gradient-to-br from-cream-50 to-flame-50/30 rounded-xl overflow-hidden mb-3">
      <svg viewBox="0 0 240 96" className="absolute inset-0 w-full h-full">
        {/* Candles */}
        {[
          [10, 70, 50, 40], [25, 60, 45, 35], [40, 65, 55, 45], [55, 50, 30, 25],
          [70, 55, 40, 30], [85, 40, 25, 18], [100, 45, 35, 22], [115, 30, 20, 12],
          [130, 38, 28, 18], [145, 25, 18, 8],  [160, 35, 25, 12], [175, 20, 14, 6],
          [190, 28, 18, 8],  [205, 18, 10, 4],  [220, 15, 8, 2],
        ].map(([x, hi, op, cl], i) => (
          <g key={i}>
            <line x1={x} y1={hi} x2={x} y2={hi - 10} stroke="#D85308" strokeWidth="0.8" />
            <rect x={x - 4} y={cl} width="8" height={op - cl} fill={i % 3 === 0 ? '#EF4444' : '#10B981'} rx="1" />
          </g>
        ))}
        {/* Resistance line */}
        <line x1="0" y1="20" x2="240" y2="20" stroke="#D85308" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
        <text x="6" y="16" fontSize="7" fill="#BC3F05" fontWeight="700">R: 1.0925</text>
        {/* Annotation arrow */}
        <path d="M 200 50 L 210 25" stroke="#D85308" strokeWidth="1.4" markerEnd="url(#arrow)" fill="none" />
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#D85308" />
          </marker>
        </defs>
      </svg>
    </div>
    <div className="flex items-center gap-2 text-[10px]">
      <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-bold">{isRTL ? 'استراتيجية' : 'STRATEGY'}</span>
      <span className="text-ink-600 font-bold">{isRTL ? 'كسر مقاومة + رِيتيست' : 'Breakout + retest'}</span>
    </div>
  </div>
);

/* ────────────────── small feature card ────────────────── */

const FeatureCard = ({ icon: Icon, title, body, accent = '#D85308', idx, isRTL }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: false, margin: '-60px' }}
    transition={{ duration: 0.55, delay: idx * 0.06, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -4 }}
    className="group relative bg-white rounded-2xl border border-flame-100/80 p-6 hover:border-flame-300 hover:shadow-[0_18px_50px_rgba(216,83,8,0.10)] transition-all duration-400 overflow-hidden"
    data-testid={`why-feature-${idx}`}
  >
    <div className="absolute -top-12 -end-12 w-32 h-32 rounded-full bg-flame-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
    <div className="relative">
      <div className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-4 transition-transform duration-400 group-hover:scale-110"
        style={{ background: `linear-gradient(135deg, ${accent}15, ${accent}05)`, color: accent }}>
        <Icon className="w-5.5 h-5.5" strokeWidth={2} />
      </div>
      <h3 className="text-[15px] font-black text-ink-900 mb-2 tracking-tight">{title}</h3>
      <p className="text-[13px] text-ink-600 leading-relaxed">{body}</p>
    </div>
  </motion.div>
);

/* ────────────────── highlight card (top 3) ────────────────── */

const HighlightCard = ({ icon: Icon, eyebrow, title, body, preview, idx, isRTL, slideFrom = 'up' }) => {
  const slideInit = {
    left: { x: -50, y: 0 },
    right: { x: 50, y: 0 },
    up: { x: 0, y: 40 },
  }[slideFrom];
  return (
    <motion.div
      initial={{ opacity: 0, ...slideInit }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: false, margin: '-80px' }}
      transition={{ duration: 0.7, delay: idx * 0.12, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className="group relative bg-white rounded-3xl border border-flame-100/80 p-6 lg:p-7 hover:border-flame-300 hover:shadow-[0_30px_80px_rgba(216,83,8,0.12)] transition-all duration-500 overflow-hidden"
      data-testid={`why-highlight-${idx}`}
    >
      {/* corner glow */}
      <div className="absolute -top-20 -end-20 w-60 h-60 rounded-full bg-flame-100/50 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

      <div className="relative">
        <div className="flex items-center justify-between mb-5">
          <div className="inline-flex w-12 h-12 rounded-2xl items-center justify-center bg-gradient-to-br from-flame-500 to-flame-600 text-white shadow-md group-hover:scale-110 transition-transform duration-400">
            <Icon className="w-5.5 h-5.5" strokeWidth={2} />
          </div>
          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-flame-700 bg-flame-50 px-3 py-1 rounded-full">
            {eyebrow}
          </span>
        </div>

        <h3 className="varo-display text-2xl lg:text-[26px] text-ink-900 leading-tight mb-3">{title}</h3>
        <p className="text-[14px] text-ink-600 leading-relaxed mb-6 min-h-[60px]">{body}</p>

        {/* Visual preview */}
        <div className="mt-auto">{preview}</div>
      </div>
    </motion.div>
  );
};

/* ────────────────── main section ────────────────── */

const WhyVarotradesFeatures = () => {
  const { isRTL } = useLanguage();

  const highlights = [
    {
      icon: TrendingUp,
      eyebrow: isRTL ? 'يومياً' : 'Daily',
      title: isRTL ? 'أفكار تداول يومية' : 'Daily Trading Ideas',
      body: isRTL
        ? 'إشارات وأفكار يومية على فوركس، ذهب، كريبتو ومؤشرات — مع نقطة دخول واضحة، هدف ربح، ووقف خسارة.'
        : 'Daily ideas on forex, gold, crypto and indices — each with a clear entry, take-profit and stop-loss.',
      preview: <SignalPreview symbol="XAU/USD" side="BUY" isRTL={isRTL} />,
      slideFrom: isRTL ? 'right' : 'left',
    },
    {
      icon: Users,
      eyebrow: isRTL ? 'للمبتدئين' : 'Beginners',
      title: isRTL ? 'مجتمع المبتدئين' : 'Beginner Trading Community',
      body: isRTL
        ? 'مساحة آمنة لطرح الأسئلة، تعلّم الأساسيات والنمو خطوة بخطوة مع متداولين في نفس مرحلتك.'
        : 'A safe space to ask questions, learn the basics, and grow step-by-step alongside traders at your level.',
      preview: <BeginnerCommunityPreview isRTL={isRTL} />,
      slideFrom: 'up',
    },
    {
      icon: Briefcase,
      eyebrow: isRTL ? 'للمحترفين' : 'Pro',
      title: isRTL ? 'مجتمع المتداولين المحترفين' : 'Professional Trading Community',
      body: isRTL
        ? 'نقاش متقدّم، تحليلات فنية، مشاركة استراتيجيات، وتشريح صفقات حقيقية مع كبار المتداولين.'
        : 'Advanced discussion, technical setups, strategy sharing, and real-trade breakdowns with senior traders.',
      preview: <ProCommunityPreview isRTL={isRTL} />,
      slideFrom: isRTL ? 'left' : 'right',
    },
  ];

  const features = [
    {
      icon: GraduationCap,
      title: isRTL ? 'تعليم تداول متكامل' : 'Trading Education',
      body: isRTL
        ? 'مكتبة من المقالات، الكتب الإلكترونية، وقاموس مالي — من الأساسيات إلى إدارة المخاطر وهيكل السوق.'
        : 'Articles, e-books, and a financial glossary — from basics to risk management and market structure.',
      accent: '#D85308',
    },
    {
      icon: MonitorPlay,
      title: isRTL ? 'منصّات تداول متقدّمة' : 'Advanced Trading Platforms',
      body: isRTL
        ? 'تنفيذ < 30ms، أدوات رسم بياني احترافية، Market Watch، تداول متعدّد الأصول، ويب وموبايل.'
        : 'Sub-30ms execution, pro charts, market watch, multi-asset trading, web and mobile.',
      accent: '#BC3F05',
    },
    {
      icon: Newspaper,
      title: isRTL ? 'تحليل سوق وأخبار' : 'Market Analysis & News',
      body: isRTL
        ? 'تحديثات حيّة، أخبار مالية مرخّصة، تقويم اقتصادي، وشرح تحرّكات السوق المهمّة.'
        : 'Live updates, licensed financial news, an economic calendar, and explanations of key market moves.',
      accent: '#D85308',
    },
    {
      icon: Headset,
      title: isRTL ? 'دعم مخصّص' : 'Dedicated Support',
      body: isRTL
        ? 'دعم حيّ، واتساب، وفريق علاقات عملاء يتحدّث لغتك — للمبتدئين والمحترفين على حد سواء.'
        : 'Live chat, WhatsApp, and an account team that speaks your language — for beginners and pros alike.',
      accent: '#FF7A00',
    },
  ];

  return (
    <section
      className="relative py-20 lg:py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FAF7F2 45%, #FFFAF3 100%)' }}
      data-testid="why-varotrades-section"
    >
      {/* Decorative blooms */}
      <div className="absolute -top-32 -end-32 w-[400px] h-[400px] rounded-full bg-flame-100/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -start-32 w-[400px] h-[400px] rounded-full bg-flame-50/60 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1280px] mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: '-50px' }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-flame-50 border border-flame-200 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-flame-600" />
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-flame-700">
              {isRTL ? 'لماذا VAROTRADES' : 'Why VAROTRADES'}
            </span>
          </div>
          <h2 className="varo-display varo-display-md text-ink-900">
            {isRTL ? <>لماذا التداول مع <span className="varo-text-flame">VARO Trades</span>؟</> : <>Why trade with <span className="varo-text-flame">VARO Trades</span>?</>}
          </h2>
          <p className="mt-5 text-ink-600 text-[15.5px] lg:text-[16px] leading-relaxed">
            {isRTL
              ? 'كل ما تحتاجه لتتداول بذكاء، أسرع، وبثقة أكبر — من التعليم والرؤى اليومية إلى الأدوات المتقدّمة، دعم الخبراء، والوصول إلى الأسواق العالمية.'
              : 'Everything you need to trade smarter, faster, and with more confidence — from education and daily insights to advanced tools, expert support, and global market access.'}
          </p>
        </motion.div>

        {/* Top 3 highlight cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 mb-12">
          {highlights.map((h, i) => (
            <HighlightCard key={i} idx={i} isRTL={isRTL} {...h} />
          ))}
        </div>

        {/* Mid 7 feature cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <FeatureCard key={i} idx={i} isRTL={isRTL} {...f} />
          ))}
          {/* Slot 8 — composite "All-in-one" call-out */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, margin: '-60px' }}
            transition={{ duration: 0.55, delay: 7 * 0.06 }}
            whileHover={{ y: -4 }}
            className="relative bg-gradient-to-br from-ink-900 via-[#1a1410] to-flame-700 rounded-2xl p-6 text-white overflow-hidden group"
          >
            <div className="absolute -top-10 -end-10 w-40 h-40 rounded-full bg-flame-500/30 blur-2xl group-hover:bg-flame-500/50 transition-all duration-500" />
            <div className="relative">
              <div className="inline-flex w-12 h-12 rounded-2xl bg-white/10 backdrop-blur items-center justify-center mb-4">
                <BarChart3 className="w-5.5 h-5.5 text-flame-300" />
              </div>
              <h3 className="text-[15px] font-black mb-2 tracking-tight">
                {isRTL ? 'كل شيء في مكان واحد' : 'All in one place'}
              </h3>
              <p className="text-[13px] text-white/75 leading-relaxed mb-5">
                {isRTL
                  ? 'لا تحتاج 5 منصّات. VAROTRADES يجمع التداول، التعليم، الإشارات، والمجتمع في حساب واحد.'
                  : 'No need for 5 platforms. VAROTRADES bundles trading, education, signals, and community into one account.'}
              </p>
              <Link to="/register" className="inline-flex items-center gap-1.5 text-[12px] font-bold text-flame-300 hover:text-flame-200 transition-colors">
                {isRTL ? 'افتح حساباً' : 'Open an account'}
                <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA row */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: '-50px' }}
          transition={{ duration: 0.65, delay: 0.1 }}
          className="mt-14 lg:mt-16"
        >
          <div className="relative rounded-3xl overflow-hidden p-8 lg:p-10 bg-gradient-to-br from-flame-500 via-flame-600 to-[#8a2e04] shadow-[0_30px_80px_rgba(216,83,8,0.25)]">
            {/* Pattern */}
            <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
              backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1.5px), radial-gradient(circle at 70% 60%, white 1px, transparent 1.5px), radial-gradient(circle at 40% 80%, white 1px, transparent 1.5px)',
              backgroundSize: '40px 40px, 60px 60px, 50px 50px',
            }} />

            <div className={`relative flex flex-col lg:flex-row items-center justify-between gap-6 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
              <div className={`flex-1 ${isRTL ? 'text-right lg:text-right' : 'text-left lg:text-left'} text-center`}>
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/85 mb-2">
                  {isRTL ? 'ابدأ الآن' : 'Get started'}
                </p>
                <h3 className="varo-display text-2xl lg:text-3xl text-white leading-tight">
                  {isRTL ? 'ابدأ رحلتك في التداول مع VARO Trades اليوم' : 'Start your trading journey with VARO Trades today'}
                </h3>
                <p className="mt-2 text-[13.5px] text-white/85 max-w-xl">
                  {isRTL
                    ? 'افتح حساباً حيّاً وابدأ التداول، أو جرّب الحساب التجريبي بدون أي مخاطرة.'
                    : 'Open a live account and start trading — or try the free demo with zero risk.'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch gap-3 w-full lg:w-auto">
                <Link
                  to="/register"
                  data-testid="why-cta-live"
                  className="inline-flex items-center justify-center gap-2 h-13 px-7 rounded-2xl bg-white text-flame-700 font-black text-[14px] hover:scale-[1.03] hover:shadow-xl transition-all"
                >
                  {isRTL ? 'افتح حساباً حيّاً' : 'Open Live Account'}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                </Link>
                <Link
                  to="/register?type=demo"
                  data-testid="why-cta-demo"
                  className="inline-flex items-center justify-center gap-2 h-13 px-7 rounded-2xl bg-ink-900/40 backdrop-blur border border-white/30 text-white font-black text-[14px] hover:bg-ink-900/60 transition-all"
                >
                  {isRTL ? 'جرّب حساباً تجريبياً' : 'Try Demo Account'}
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyVarotradesFeatures;
